<script setup>
import { ref, onMounted } from 'vue';
import { getRecentActivePets, getPopularPets } from '@/services/petService';

const newlyAddedPets = ref([]);
const popularPets = ref([]);

const fetchPets = async () => {
  try {
    // Fetch Newly Added Pets (last 4 active pets)
    const newSnapshot = await getRecentActivePets(4);
    newlyAddedPets.value = newSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      displayImage: doc.data().images?.[doc.data().coverImageIndex || 0] || doc.data().images?.[0] || '/images/pet_placeholder.jpg'
    }));

    // Fetch Popular Pets (4 active pets with most views)
    const popSnapshot = await getPopularPets(4);
    popularPets.value = popSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      displayImage: doc.data().images?.[doc.data().coverImageIndex || 0] || doc.data().images?.[0] || '/images/pet_placeholder.jpg'
    }));
  } catch (err) {
    console.error("Error fetching homepage pets:", err);
  }
};

onMounted(() => {
  fetchPets();
});
</script>

<template>
  <section class="anasayfa most_product_area">
    <div
      class="main_box"
      style="padding: 50px 15px; max-width: 1400px; margin: auto;"
    >
      <div class="homepage-grid">
        <!-- SOL: NEWLY ADDED (PETS) -->
        <div class="column left-section">
          <h2>🛎️ Newly Added</h2>
          <p class="slogan">
            Discover the latest pet listings.
          </p>

          <transition-group
            name="fade-slide"
            tag="div"
            class="pet-cards"
          >
            <router-link
              v-for="pet in newlyAddedPets"
              :key="pet.id"
              :to="{ name: 'pet-details', params: { id: pet.id } }"
              class="pet-card text-decoration-none text-dark"
            >
              <img
                :src="pet.displayImage"
                :alt="pet.title"
                class="pet-image"
              >
              <div class="pet-info">
                <h3 class="pet-name">
                  {{ pet.title?.length > 20 ? pet.title.substring(0, 20) + '...' : pet.title }}
                </h3>
                <p
                  class="pet-desc text-truncate"
                  style="max-width: 200px;"
                >
                  {{ pet.category }}
                </p>
                <p class="pet-region">
                  {{ pet.location?.city || pet.location?.country || '' }}
                </p>
                <p class="pet-price">
                  {{ pet.currency || '£' }}{{ pet.price }}
                </p>
              </div>
            </router-link>
          </transition-group>

          <div class="buttons left-buttons">
            <router-link to="/pets">
              <button>Show All</button>
            </router-link>
          </div>
        </div>

        <!-- SAĞ: POPULAR PETS -->
        <div class="column right-section">
          <h2>🐾 Popular Pets</h2>
          <p class="slogan">
            Discover the most viewed pets from our community.
          </p>

          <transition-group
            name="fade-slide"
            tag="div"
            class="pet-cards"
          >
            <router-link
              v-for="pet in popularPets"
              :key="pet.id"
              :to="{ name: 'pet-details', params: { id: pet.id } }"
              class="pet-card text-decoration-none text-dark"
            >
              <img
                :src="pet.displayImage"
                :alt="pet.title"
                class="pet-image"
              >
              <div class="pet-info">
                <h3 class="pet-name">
                  {{ pet.title?.length > 20 ? pet.title.substring(0, 20) + '...' : pet.title }}
                </h3>
                <p
                  class="pet-desc text-truncate"
                  style="max-width: 200px;"
                >
                  {{ pet.category }}
                </p>
                <p class="pet-region">
                  {{ pet.location?.city || pet.location?.country || '' }}
                </p>
                <p class="pet-price">
                  {{ pet.currency || '£' }}{{ pet.price }}
                </p>
              </div>
            </router-link>
          </transition-group>

          <div class="buttons right-buttons">
            <router-link to="/pets">
              <button>Show All</button>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.homepage-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: stretch;
}
.column {
  flex: 1 1 49%;
  padding: 10px;
}
.left-section,
.right-section {
  display: flex;
  flex-direction: column;
}
p {
  margin: 0;
}
h2 {
  font-size: 1.8rem;
  margin-bottom: 5px;
}
.slogan {
  color: #777;
  margin-bottom: 20px;
}
.pet-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  height: 100%;
  flex-grow: 1;
}
.pet-card {
  display: flex;
  align-items: center;
  width: 48%;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  padding: 10px;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
}
.pet-card:hover {
  transform: scale(1.02);
  box-shadow: 0 12px 25px rgba(0, 0, 0, 0.2);
}
.pet-image {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
  margin-right: 15px;
}
.pet-info {
  flex: 1;
  overflow: hidden;
}
.pet-name {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pet-desc {
  font-size: 0.9rem;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pet-region,
.pet-price {
  font-size: 0.85rem;
  color: #888;
  margin-top: 2px;
}
.buttons {
  margin-top: 15px;
  display: flex;
  gap: 10px;
}
.buttons button {
  padding: 8px 16px;
  font-size: 0.95rem;
  border: none;
  background-color: #e91e63;
  color: #fff;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}
.buttons button:hover {
  background-color: #c2185b;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.4s ease;
}
@media (max-width: 1024px) {
  .homepage-grid {
    flex-direction: column;
  }
  .pet-card {
    width: 100%;
    flex-direction: column;
    text-align: center;
  }
  .pet-image {
    width: 100%;
    height: 160px;
    margin-right: 0;
  }
}
</style>